import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, CircularProgress, Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { ProfileDisplay } from '../components/ProfileDisplay';
import { ProfileEditForm } from '../components/ProfileEditForm';

/**
 * Profile page component for viewing and editing user profile.
 * Refactored to use TanStack Query and container/presentational pattern.
 */
const Profile = () => {
  const { userId, authToken } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  
  const { user, isLoading, isError, error, updateProfile, isUpdating, updateError } = useUserProfile(userId);

  // Redirect to login if not authenticated
  if (!authToken) {
    navigate('/login');
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (isError || !user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || 'Error loading profile. Please try again.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {isEditing ? (
        <ProfileEditForm
          user={user}
          onCancel={() => setIsEditing(false)}
          onSuccess={() => setIsEditing(false)}
          updateProfile={updateProfile}
          isUpdating={isUpdating}
        />
      ) : (
        <ProfileDisplay user={user} onEdit={() => setIsEditing(true)} />
      )}
    </Container>
  );
};

export default Profile;
